import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, SlidersHorizontal, Upload, Download, Loader2, X, Search, LayoutGrid, List } from 'lucide-react';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { Button } from '@/components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import HIRARiskBandSettings from './HIRARiskBandSettings';
import { DEFAULT_RISK_BANDS, fetchRiskBandConfig, getBandColorForLabel } from '../../utils/riskBands';
import { departmentLabel } from '../../utils/departmentLabel';
import { formatDate } from '../../utils/dateUtils';

const HIRAList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError } = useAlert();
  const { hasPermission } = usePermissions();
  const { activeSiteId, availableSites } = useSite();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  // Tenant risk bands (badge colors) + admin flag for the settings modal
  const [riskBands, setRiskBands] = useState(DEFAULT_RISK_BANDS);
  const [canManageBands, setCanManageBands] = useState(false);
  const [showBandSettings, setShowBandSettings] = useState(false);

  // EHSMS-F-12 Excel import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importTitle, setImportTitle] = useState('');
  const [importSiteId, setImportSiteId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Blank EHSMS-F-12 register with an INSTRUCTIONS sheet — generated
  // server-side by the same writer as the Excel export, so it always matches
  // what the importer accepts.
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/risk-assessments/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'HIRA_Import_Template_EHSMS-F-12.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to download the import template. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const openImportDialog = () => {
    setImportFile(null);
    setImportTitle('');
    setImportSiteId(activeSiteId || '');
    setImportErrors([]);
    setImportWarnings([]);
    setShowImportDialog(true);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportErrors([]);
    setImportWarnings([]);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', importFile);
      if (importTitle.trim()) form.append('title', importTitle.trim());
      if (importSiteId) form.append('site_id', importSiteId);
      const response = await axios.post(`${API}/risk-assessments/import-excel`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowImportDialog(false);
      navigate(`/hira/${response.data.id}`);
    } catch (err) {
      // All-or-nothing: 422 carries the FULL per-row error list — render it
      // in the dialog, not a toast.
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'IMPORT_VALIDATION_FAILED') {
        setImportErrors(detail.errors || []);
        setImportWarnings(detail.warnings || []);
      } else if (typeof detail === 'string') {
        setImportErrors([detail]);
      } else {
        setImportErrors(['Import failed. Please check the file and try again.']);
      }
    } finally {
      setImporting(false);
    }
  };

  // Pagination — mirrors the pattern in ptw/PTWList.js + safety/IncidentIntelligence.js
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Client-side search + filters (the page already holds the full array)
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('All');
  const [residualFilter, setResidualFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');

  // Grid ↔ table view; remembered per browser. Anything except 'table'
  // (including tampered values) falls back to the grid default.
  const [viewMode, setViewModeState] = useState(() =>
    localStorage.getItem('hira_list_view') === 'table' ? 'table' : 'grid'
  );
  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem('hira_list_view', mode);
  };

  useEffect(() => {
    fetchAssessments();
  }, [filter, activeSiteId]);

  useEffect(() => {
    fetchRiskBandConfig().then(cfg => {
      setRiskBands(cfg.bands || DEFAULT_RISK_BANDS);
      setCanManageBands(!!cfg.can_manage);
    });
  }, []);

  const clearFilters = () => {
    setSearchTerm('');
    setRiskFilter('All');
    setResidualFilter('All');
    setDepartmentFilter('All');
    setFilter('All');
  };

  const filteredAssessments = assessments.filter(ra =>
    (!searchTerm.trim() || (ra.title || '').toLowerCase().includes(searchTerm.trim().toLowerCase()))
    && (riskFilter === 'All' || (ra.overall_risk_level || 'N/A') === riskFilter)
    && (residualFilter === 'All' || (ra.highest_residual_risk_level || 'Not assessed') === residualFilter)
    && (departmentFilter === 'All' || departmentLabel(ra) === departmentFilter)
  );

  // Filter options: risk levels from tenant bands (never hard-coded);
  // departments derived from the loaded rows (HIRA departments are free text).
  const bandLabels = riskBands.map(b => b.label);
  const departmentOptions = [...new Set(assessments.map(departmentLabel))].sort();

  // Reset to page 1 when filters or the underlying list change.
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, activeSiteId, pageSize, assessments.length, searchTerm, riskFilter, residualFilter, departmentFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredAssessments.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredAssessments.length);
  const paginatedAssessments = filteredAssessments.slice(pageStart, pageEnd);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter !== 'All') params.append('status', filter);
      if (activeSiteId) params.append('site_id', activeSiteId);
      const url = params.toString()
        ? `${API}/risk-assessments?${params.toString()}`
        : `${API}/risk-assessments`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(response.data);
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-gray-100 text-gray-700 border-gray-500';
      case 'Under Review': return 'bg-blue-100 text-blue-700 border-blue-500';
      case 'Active': return 'bg-green-100 text-green-700 border-green-500';
      case 'Archived': return 'bg-red-100 text-red-700 border-red-500';
      default: return 'bg-gray-100 text-gray-700 border-gray-500';
    }
  };

  // Band colors are tenant-configurable — resolve from the band config
  // (legacy labels fall back to their historical colors).
  const getRiskBadgeStyle = (level) => ({
    backgroundColor: getBandColorForLabel(level, riskBands),
    color: '#FFFFFF',
  });


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Back to Dashboard */}
        <Button variant="outline" onClick={() => navigate('/safety')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">HIRA - Risk Assessments</h1>
            <p className="text-gray-600 mt-1">
              {filteredAssessments.length === assessments.length
                ? `${assessments.length} assessment(s) found`
                : `${filteredAssessments.length} of ${assessments.length} assessment(s) match`}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {canManageBands && (
              <Button
                variant="outline"
                onClick={() => setShowBandSettings(true)}
                title="Configure tenant risk bands and severity/probability wording"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Risk Settings
              </Button>
            )}
            {hasPermission('hira', 'create') && (
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                title="Download the blank EHSMS-F-12 register (with instructions) to fill in and import"
              >
                {downloadingTemplate
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Download Template
              </Button>
            )}
            {hasPermission('hira', 'create') && (
              <Button
                variant="outline"
                onClick={openImportDialog}
                title="Import a legacy EHSMS-F-12 Excel register as a new Draft assessment"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Excel
              </Button>
            )}
            {hasPermission('hira', 'create') && (
            <button
              onClick={() => navigate('/hira/create')}
              className="bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Risk Assessment
            </button>
            )}
          </div>
        </div>

        {/* Filters — BUG-286: hide Draft chip for read-only users (no hira:create).
            Backend enforces the same rule; hiding the chip avoids a dead click. */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            {['All', 'Draft', 'Under Review', 'Active', 'Archived']
              .filter(status => status !== 'Draft' || hasPermission('hira', 'create'))
              .map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    filter === status
                      ? 'bg-[#1B5C94] text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status}
                </button>
              ))}
            <div className="ml-auto flex items-center gap-3 text-sm">
              {/* Grid ↔ table toggle (persisted per browser) */}
              <div className="flex items-center rounded-lg overflow-hidden border border-gray-300">
                <button
                  onClick={() => setViewMode('grid')}
                  title="Card view"
                  className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-[#1B5C94] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  title="List view"
                  className={`px-3 py-2 ${viewMode === 'table' ? 'bg-[#1B5C94] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              <span className="text-gray-600">
                {filteredAssessments.length === 0
                  ? 'No assessments'
                  : `Showing ${pageStart + 1}-${pageEnd} of ${filteredAssessments.length}`}
              </span>
              <label className="flex items-center gap-1 text-gray-600">
                Page size
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Search + attribute filters (client-side over the loaded list) */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Highest Risk</label>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
              >
                <option value="All">All</option>
                {bandLabels.map(l => <option key={l} value={l}>{l}</option>)}
                <option value="N/A">N/A</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Residual Risk</label>
              <select
                value={residualFilter}
                onChange={(e) => setResidualFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
              >
                <option value="All">All</option>
                {bandLabels.map(l => <option key={l} value={l}>{l}</option>)}
                <option value="Not assessed">Not assessed</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Department</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                >
                  <option value="All">All</option>
                  {departmentOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {(searchTerm || riskFilter !== 'All' || residualFilter !== 'All' || departmentFilter !== 'All' || filter !== 'All') && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-[#1B5C94] hover:underline whitespace-nowrap"
                >
                  Reset All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Assessments */}
        {assessments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Risk Assessments Yet</h3>
            <p className="text-gray-600 mb-4">Create your first risk assessment to identify and manage workplace hazards.</p>
            {hasPermission('hira', 'create') && (
              <button
                onClick={() => navigate('/hira/create')}
                className="bg-[#1B5C94] text-white font-bold py-2 px-6 rounded-lg hover:bg-[#164A7B] transition-colors"
              >
                Create First Assessment
              </button>
            )}
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-10 text-center">
            <h3 className="text-lg font-bold text-gray-800 mb-2">No assessments match the current filters</h3>
            <p className="text-gray-600 mb-4">Try a different search term or clear the filters.</p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-[#1B5C94] text-[#1B5C94] font-semibold rounded-lg hover:bg-blue-50"
            >
              Reset All Filters
            </button>
          </div>
        ) : viewMode === 'table' ? (
          <>
            <div className="hidden md:block bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white">
                    <tr>
                      {['Title', 'Status', 'Site', 'Department', 'Hazards', 'Highest Risk', 'Residual Risk', 'Assessor', 'Created'].map(col => (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedAssessments.map(ra => (
                      <tr
                        key={ra.id}
                        onClick={() => navigate(`/hira/${ra.id}`)}
                        className="hover:bg-blue-50 cursor-pointer"
                      >
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                          {ra.title}
                          {ra.version_number && (
                            <span className="ml-2 px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full text-[10px] font-bold">v{ra.version_number}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(ra.status)}`}>
                            {ra.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{ra.site_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{departmentLabel(ra)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800 text-center">{ra.total_hazards || 0}</td>
                        <td className="px-4 py-3">
                          {(ra.overall_risk_level && ra.overall_risk_level !== 'N/A') ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={getRiskBadgeStyle(ra.overall_risk_level)}>
                              {ra.overall_risk_level}
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-600">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {ra.highest_residual_risk_level ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={getRiskBadgeStyle(ra.highest_residual_risk_level)}>
                              {ra.highest_residual_risk_level}
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-600">Not assessed</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{ra.assessor_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {/* REG-11: tenant date format + timezone */}
                          {ra.created_at ? formatDate(ra.created_at) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Small screens fall back to cards */}
            <div className="md:hidden grid grid-cols-1 gap-6">
              {paginatedAssessments.map(ra => (
                <div
                  key={ra.id}
                  onClick={() => navigate(`/hira/${ra.id}`)}
                  className="bg-white rounded-xl shadow-md p-6 cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-bold text-gray-800 flex-1">{ra.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(ra.status)}`}>
                      {ra.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{departmentLabel(ra)} · {ra.total_hazards || 0} hazard(s)</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedAssessments.map(ra => (
              <div
                key={ra.id}
                onClick={() => navigate(`/hira/${ra.id}`)}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-[#1B5C94]"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-gray-800 flex-1">{ra.title}</h3>
                  <div className="flex items-center gap-2">
                    {ra.version_number && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">
                        v{ra.version_number}
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(ra.status)}`}>
                      {ra.status}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {ra.site_name && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-medium text-blue-700">{ra.site_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>{departmentLabel(ra)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{ra.location || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{ra.assessor_name}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Hazards Identified</p>
                    <p className="text-2xl font-bold text-gray-800">{ra.total_hazards || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Highest Risk</p>
                    <span className="px-3 py-1 rounded-full text-xs font-bold" style={getRiskBadgeStyle(ra.overall_risk_level || 'N/A')}>
                      {ra.overall_risk_level || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Residual</p>
                    {ra.highest_residual_risk_level ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={getRiskBadgeStyle(ra.highest_residual_risk_level)}>
                        {ra.highest_residual_risk_level}
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-600">Not assessed</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination — hidden when everything fits on one page. */}
        {filteredAssessments.length > pageSize && (
          <div className="bg-white rounded-xl shadow-sm p-3 mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="text-gray-600">
              Page <span className="font-semibold">{safePage}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={safePage === 1}
                className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                « First
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹ Prev
              </button>
              {(() => {
                const window_ = 2;
                let start = Math.max(1, safePage - window_);
                let end = Math.min(totalPages, start + 4);
                start = Math.max(1, end - 4);
                const pages = [];
                for (let p = start; p <= end; p++) pages.push(p);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded border text-sm ${
                      p === safePage
                        ? 'bg-[#1B5C94] text-white border-[#1B5C94]'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={safePage === totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
        <HIRARiskBandSettings open={showBandSettings} onClose={() => setShowBandSettings(false)} />

        {/* EHSMS-F-12 Excel import dialog */}
        {showImportDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-bold text-gray-900">Import HIRA Excel Register</h2>
                </div>
                <button onClick={() => setShowImportDialog(false)} className="text-gray-400 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Download the template, <span className="font-semibold">delete the shaded example rows</span>,
                  then fill in one hazard per row from row 7 of the <span className="font-mono">HIRA</span> sheet.
                  Columns E, O and P are mandatory. All rows are validated first — if any row fails, nothing is imported.
                </p>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-semibold text-gray-700">Workbook (.xlsx)</label>
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate}
                      className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 disabled:opacity-50"
                    >
                      {downloadingTemplate
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Download className="w-4 h-4" />}
                      Download template
                    </button>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportErrors([]); e.target.value = ''; }}
                    className="w-full text-sm text-gray-700"
                  />
                  {importFile && (
                    <p className="text-xs text-gray-500 mt-1">Selected: {importFile.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Assessment title (optional)</label>
                  <input
                    type="text"
                    value={importTitle}
                    onChange={(e) => setImportTitle(e.target.value)}
                    placeholder="Defaults to the file name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                {(availableSites || []).length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Site</label>
                    <select
                      value={importSiteId}
                      onChange={(e) => setImportSiteId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">-- Select site --</option>
                      {availableSites.map(s => (
                        <option key={s.site_id} value={s.site_id}>{s.site_name || s.site_id}</option>
                      ))}
                    </select>
                  </div>
                )}

                {importWarnings.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm space-y-1">
                    {importWarnings.map((w, i) => <p key={i}>{w}</p>)}
                  </div>
                )}

                {importErrors.length > 0 && (
                  <div className="border border-red-300 rounded-lg">
                    <p className="px-3 py-2 bg-red-50 text-red-800 text-sm font-semibold border-b border-red-200">
                      Import failed validation — nothing was created ({importErrors.length} issue{importErrors.length === 1 ? '' : 's'}):
                    </p>
                    <div className="max-h-48 overflow-y-auto px-3 py-2 space-y-1">
                      {importErrors.map((err, i) => (
                        <p key={i} className="text-xs text-red-700">{err}</p>
                      ))}
                    </div>
                    <p className="px-3 py-2 border-t border-red-200 text-xs text-gray-600">
                      Wrong layout?{' '}
                      <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        disabled={downloadingTemplate}
                        className="text-blue-600 hover:text-blue-800 font-semibold underline disabled:opacity-50"
                      >
                        Download the template
                      </button>{' '}
                      and copy your data into its <span className="font-mono">HIRA</span> sheet — see the
                      INSTRUCTIONS sheet for what each column accepts.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
                <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
                <Button
                  type="button"
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default HIRAList;
